/**
 * DashboardView.jsx
 *
 * Purpose: Manager overview - KPIs, active projects, engineer status, capacity, utilization.
 * Layout: every block is a widget on the customizable grid (10/12/26) -
 *         components/dashboard/layout/ (DashboardGrid, dashboardLayout.js),
 *         saved per person (/api/dashboard-layout). This file builds the
 *         widgets; the grid places them.
 * Data: reads projectsThisYear, allEngineers, allProjects, selectedYear from AppContext
 * API: none directly (reads from context which fetches /api/*); its cards fetch
 *      their own (the "last week" invoices box: /api/finance/invoices)
 * Connected to: All views - KPIs reflect live data from every entity
 */

import { useState, useMemo } from 'react';
import { Icon } from '../components/ui/Icon';
import { UtilizationChart } from '../components/charts/UtilizationChart';
import { useApp } from '../context/AppContext';
import { fmt } from '../utils/format';
import MyActionsCard from '../components/crm/MyActionsCard';
import NoNextStepCard from '../components/crm/NoNextStepCard';
import TasksCard from '../components/tasks/TasksCard';
import { useCalendarUtilization, CALENDAR_FAILED } from '../hooks/useCalendarUtilization';
import ActiveProjectsCard from '../components/dashboard/ActiveProjectsCard';
import QuickAddMenu from '../components/tasks/QuickAddMenu';
import { isActiveProject, isClosedProject } from '../data/projectStatus';
import { healthOf } from '../data/projectHealth';
import { useLastWeekBoxes } from '../components/dashboard/LastWeekBoxes';
import KpiCard from '../components/dashboard/KpiCard';
import DashboardGrid from '../components/dashboard/layout/DashboardGrid';
import { useDashboardLayout } from '../components/dashboard/layout/useDashboardLayout';
import { useAuth } from '../context/AuthContext';

const CARD = {
  background: '#fff',
  borderRadius: 12,
  border: '1px solid #ECECEC',
  boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
};

/* ===== Compare-mode side-by-side dashboard =====
   Renders one self-contained narrow dashboard column per selected year. */
function CompareColumn({ year, allProjects, allEngineers, onNavigate }) {
  const yearStr = String(year);
  const inYear = (d) => typeof d === 'string' && d.startsWith(yearStr);

  const yearProjects = useMemo(
    () => (allProjects || []).filter((p) => inYear(p.quoteDate || p.quote_date)),
    [allProjects, yearStr] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const won = yearProjects.filter((p) => (p.status || '').toLowerCase() === 'won');
  const lost = yearProjects.filter((p) => (p.status || '').toLowerCase() === 'lost');
  const open = yearProjects.filter((p) => (p.status || '').toLowerCase() === 'open');
  const closed = won.length + lost.length;
  const winRate = closed > 0 ? ((won.length / closed) * 100).toFixed(2) : '-';
  const pipelineValue = open.reduce((s, p) => {
    const v = parseFloat(p.quotedPrice || p.quoted_price || 0);
    return s + (isFinite(v) && v < 500000000 ? v : 0);
  }, 0);
  const wonValue = won.reduce((s, p) => {
    const v = parseFloat(p.quotedPrice || p.quoted_price || 0);
    return s + (isFinite(v) && v < 500000000 ? v : 0);
  }, 0);

  // Active Won (top 5) for this year, FANUC only.
  const activeWon = useMemo(() => {
    const w = won.filter((p) => !isClosedProject(p) && /fanuc/i.test(p.customer || ''));
    const scoreOf = (p) => p.poDateIssued || p.po_date_issued || p.quoteDate || p.quote_date || '';
    return w.sort((a, b) => scoreOf(b).localeCompare(scoreOf(a))).slice(0, 5);
  }, [won]);

  // Engineers actively assigned to a FANUC project that has work in this year
  const engineers = useMemo(() => {
    return (allEngineers || [])
      .filter((e) => {
        if (e.is_archived || e.isArchived) return false;
        const firstName = (e.name || '').split(' ')[0].trim();
        if (!firstName) return false;
        const re = new RegExp(`\\b${firstName.toLowerCase().replace(/[.*+?^${}()|[\\]\\\\]/g, '\\\\$&')}\\b`, 'i');
        return yearProjects.some(
          (p) => p.engineer && re.test(p.engineer) && (p.status === 'Won' || p.status === 'Open') && /fanuc/i.test(p.customer || '')
        );
      })
      .slice(0, 8);
  }, [allEngineers, yearProjects]);

  const projectForEng = (engineer) => {
    const firstName = (engineer.name || '').split(' ')[0].trim();
    if (!firstName) return null;
    const re = new RegExp(`\\b${firstName.toLowerCase().replace(/[.*+?^${}()|[\\]\\\\]/g, '\\\\$&')}\\b`, 'i');
    const matches = yearProjects.filter((p) => p.engineer && re.test(p.engineer) && (p.status === 'Won' || p.status === 'Open'));
    return matches.sort((a, b) => (b.quoteDate || '').localeCompare(a.quoteDate || ''))[0] || null;
  };

  const KPI = ({ label, value, accent, sub }) => (
    <div
      style={{
        background: '#fff',
        border: '1px solid #ECECEC',
        borderRadius: 10,
        padding: '12px 14px',
        position: 'relative',
        overflow: 'hidden',
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
      }}
    >
      <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 3, background: accent }} />
      <div style={{ paddingLeft: 8 }}>
        <div style={{ fontSize: 9, fontWeight: 800, color: '#888', letterSpacing: 1.4, textTransform: 'uppercase' }}>{label}</div>
        <div style={{ fontSize: 22, fontWeight: 800, color: '#0D0D0D', lineHeight: 1.1, marginTop: 4, fontVariantNumeric: 'tabular-nums' }}>
          {value}
        </div>
        {sub && <div style={{ fontSize: 10.5, color: '#777', marginTop: 2 }}>{sub}</div>}
      </div>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div
        style={{
          background: 'linear-gradient(135deg, #1B5E20 0%, #2E7D32 100%)',
          color: '#fff',
          borderRadius: 10,
          padding: '12px 16px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
        }}
      >
        <div style={{ fontSize: 18, fontWeight: 900, fontFamily: 'Playfair Display, serif' }}>{year}</div>
        <div style={{ fontSize: 10.5, fontWeight: 700, opacity: 0.85, letterSpacing: 1.2, textTransform: 'uppercase' }}>
          {yearProjects.length} bids · {won.length}W · {lost.length}L
        </div>
      </div>

      <KPI label="Pipeline Value" value={pipelineValue ? fmt(pipelineValue) : '$0'} accent="#1B5E20" sub={`${open.length} open`} />
      <KPI label="Won Value" value={wonValue ? fmt(wonValue) : '$0'} accent="#43A047" sub={`${won.length} deals closed`} />
      <KPI label="Open Opportunities" value={open.length} accent="#1565C0" />
      <KPI
        label="Win Rate"
        value={typeof winRate === 'string' && winRate === '-' ? '-' : `${winRate}%`}
        accent="#E65100"
        sub={`${won.length}W · ${lost.length}L`}
      />

      {/* Active Won mini-table */}
      <div style={{ background: '#fff', border: '1px solid #ECECEC', borderRadius: 10, overflow: 'hidden' }}>
        <div style={{ padding: '10px 14px', borderBottom: '1px solid #F0F0F0', fontSize: 12, fontWeight: 700, color: '#0D0D0D' }}>
          Active Won · FANUC ({activeWon.length})
        </div>
        {activeWon.length === 0 ? (
          <div style={{ padding: 16, fontSize: 11, color: '#5A5A5A', textAlign: 'center' }}>None in {year}.</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
            <thead>
              <tr style={{ background: '#FAFBFC' }}>
                <th
                  style={{
                    textAlign: 'left',
                    padding: '6px 10px',
                    fontSize: 9,
                    fontWeight: 700,
                    color: '#888',
                    letterSpacing: 1.2,
                    textTransform: 'uppercase',
                  }}
                >
                  Project
                </th>
                <th
                  style={{
                    textAlign: 'left',
                    padding: '6px 10px',
                    fontSize: 9,
                    fontWeight: 700,
                    color: '#888',
                    letterSpacing: 1.2,
                    textTransform: 'uppercase',
                  }}
                >
                  Engineer
                </th>
                <th
                  style={{
                    textAlign: 'left',
                    padding: '6px 10px',
                    fontSize: 9,
                    fontWeight: 700,
                    color: '#888',
                    letterSpacing: 1.2,
                    textTransform: 'uppercase',
                  }}
                >
                  Code
                </th>
              </tr>
            </thead>
            <tbody>
              {activeWon.map((p, i) => (
                <tr
                  key={p.id || i}
                  onClick={() => onNavigate && onNavigate('projects', { project: p.id })}
                  style={{ borderBottom: '1px solid #F4F6F0', background: i % 2 === 0 ? '#fff' : '#FAFBF7', cursor: 'pointer' }}
                >
                  <td
                    style={{
                      padding: '7px 10px',
                      fontWeight: 700,
                      color: '#1B5E20',
                      maxWidth: 220,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {p.name || p.description}
                  </td>
                  <td style={{ padding: '7px 10px', color: '#555' }}>{p.engineer || '-'}</td>
                  <td
                    style={{
                      padding: '7px 10px',
                      fontFamily: 'JetBrains Mono, Consolas, monospace',
                      color: '#1B5E20',
                      fontWeight: 700,
                      fontSize: 10,
                    }}
                  >
                    {p.globalProjectNumber || p.global_project_number || '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Engineers mini-table */}
      <div style={{ background: '#fff', border: '1px solid #ECECEC', borderRadius: 10, overflow: 'hidden' }}>
        <div style={{ padding: '10px 14px', borderBottom: '1px solid #F0F0F0', fontSize: 12, fontWeight: 700, color: '#0D0D0D' }}>
          Engineers active in {year} ({engineers.length})
        </div>
        {engineers.length === 0 ? (
          <div style={{ padding: 16, fontSize: 11, color: '#5A5A5A', textAlign: 'center' }}>No engineer assignments in {year}.</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
            <thead>
              <tr style={{ background: '#FAFBFC' }}>
                <th
                  style={{
                    textAlign: 'left',
                    padding: '6px 10px',
                    fontSize: 9,
                    fontWeight: 700,
                    color: '#888',
                    letterSpacing: 1.2,
                    textTransform: 'uppercase',
                  }}
                >
                  Engineer
                </th>
                <th
                  style={{
                    textAlign: 'left',
                    padding: '6px 10px',
                    fontSize: 9,
                    fontWeight: 700,
                    color: '#888',
                    letterSpacing: 1.2,
                    textTransform: 'uppercase',
                  }}
                >
                  Location
                </th>
                <th
                  style={{
                    textAlign: 'left',
                    padding: '6px 10px',
                    fontSize: 9,
                    fontWeight: 700,
                    color: '#888',
                    letterSpacing: 1.2,
                    textTransform: 'uppercase',
                  }}
                >
                  Code
                </th>
              </tr>
            </thead>
            <tbody>
              {engineers.map((e, i) => {
                const proj = projectForEng(e);
                const loc = proj ? proj.subCustomer || proj.sub_customer || proj.name : e.location || '-';
                const code = proj ? proj.globalProjectNumber || proj.global_project_number || '-' : '-';
                return (
                  <tr key={e.id || i} style={{ borderBottom: '1px solid #F4F6F0', background: i % 2 === 0 ? '#fff' : '#FAFBF7' }}>
                    <td style={{ padding: '7px 10px', fontWeight: 700, color: '#0D0D0D' }}>{e.name}</td>
                    <td
                      style={{
                        padding: '7px 10px',
                        color: '#555',
                        maxWidth: 180,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {loc}
                    </td>
                    <td
                      style={{
                        padding: '7px 10px',
                        fontFamily: 'JetBrains Mono, Consolas, monospace',
                        color: '#1B5E20',
                        fontWeight: 700,
                        fontSize: 10,
                      }}
                    >
                      {code}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default function DashboardView({ onNavigate }) {
  const {
    projectsThisYear,
    allEngineers,
    allProjects,
    selectedYear,
    pipelineStats,
    compareYears,
    setCompareYears,
    loading,
    updateProject,
    resolveOwnerName,
  } = useApp();
  // Quoted / POs / $ collected / Invoices LAST WEEK, beside Open Opportunities.
  // All projects, not the year's: last week is absolute (lastWeek.js).
  const lastWeek = useLastWeekBoxes({ projects: allProjects, nameOf: resolveOwnerName });
  // this person's widget layout, saved on their account
  const { user } = useAuth();
  const { layout, save: saveLayout } = useDashboardLayout(user && user.email);

  /* Every ACTIVE project, using the one definition in src/data/projectStatus.js
     (a won job whose Closed flag is not YES). The sidebar Projects badge and the
     Projects tab's default Active chip use the same helper, so the three can
     never show a different number. The old card was scoped to FANUC and to five rows. */
  const activeProjects = useMemo(() => {
    // Worst first: red, then yellow, then everything healthy. The point of the
    // table is to see what needs attention without reading it top to bottom.
    // Within a level, most recent PO (or quote) first, as before.
    const rank = { red: 0, yellow: 1, green: 2 };
    const recency = (p) => String(p.poDateIssued || p.po_date_issued || p.quoteDate || p.quote_date || '');
    return (projectsThisYear || [])
      .filter(isActiveProject)
      .sort((a, b) => rank[healthOf(a)] - rank[healthOf(b)] || recency(b).localeCompare(recency(a)));
  }, [projectsThisYear]);

  // right-click on a row of the table above: same menu as the Projects tab
  const [quickTarget, setQuickTarget] = useState(null);
  const [toast, setToast] = useState(null);

  /* Win rate from bid tracking projects (primary) */
  const wonProjects = projectsThisYear.filter((p) => (p.status || '').toLowerCase() === 'won');
  const lostProjects = projectsThisYear.filter((p) => (p.status || '').toLowerCase() === 'lost');
  const closedProjects = wonProjects.length + lostProjects.length;
  const yearWinRate = closedProjects > 0 ? ((wonProjects.length / closedProjects) * 100).toFixed(2) : pipelineStats.winRateBidTracking;

  /* Open bids in the selected year. There is no Budget status any more (every
     Budget bid became Open on 9/29/26), so this one list feeds both the Total
     Pipeline card and the Open Opportunities card, and the sidebar
     Opportunities badge counts the same rows. It used to drop blank rows (no
     price, no customer) here only; they add $0 to the value either way, but
     that made the "open bids" line disagree with the Open Opportunities count. */
  const openBids = projectsThisYear.filter((p) => (p.status || '').toLowerCase() === 'open');
  const pipelineValue = openBids.reduce((s, p) => {
    const v = parseFloat(p.quotedPrice || p.quoted_price || 0);
    return s + (isFinite(v) && v < 500000000 ? v : 0);
  }, 0);

  // The whole engineers table - it holds the roster and nothing else now.

  /* ── Active wins: status=Won AND not yet closed AND customer is FANUC.
       Lucid/Detroit Diesel/etc. live in their own customer-specific dashboard
       widgets - this card is scoped to the FANUC book of business. ── */

  /* ── FANUC-only engineers. Match by either:
       (a) engineer.project text contains "FANUC"
       (b) any recent Won/Open project whose engineer text mentions this engineer's
           first name has a FANUC customer (cross-link via projects table)
     ── */

  /* ── Match an engineer to their current project record (Won/Open).
       Used to derive both the GLOBAL project code and the work-location for
       the dashboard's Engineers table. ── */

  // Utilization now comes from the Manpower calendar, not the utilization
  // workbook - five months (two back, this one, two forward), PTO excluded
  // from both sides, NA roster only. See utils/utilizationFromCalendar.js.
  const { data: utilizationData, failed: calendarFailed } = useCalendarUtilization();

  const yearLabel =
    selectedYear === 'all' ? 'All Time' : Array.isArray(selectedYear) ? selectedYear.join(' + ') : `Jan\u2013Dec ${selectedYear}`;
  const yearSubLabel = selectedYear === 'all' ? 'all-time' : Array.isArray(selectedYear) ? selectedYear.join(' + ') : selectedYear;

  const kpis = [
    {
      key: 'pipeline',
      label: 'Total Pipeline',
      value: fmt(pipelineValue) === '-' ? '$0' : fmt(pipelineValue),
      sub: `${openBids.length} open bids \u00B7 ${yearWinRate}% win rate`,
      icon: 'dollar',
      accent: '#1B5E20',
      iconBg: 'rgba(27,94,32,0.1)',
      yearLabel,
      explain: {
        title: 'Total Pipeline',
        body: 'Live $ value of bids you have not yet won or lost - only Open bids in the selected year. Once a bid closes (Won, Lost, Cancelled or No Quote) it leaves the pipeline, so this number can be small even in years where you won a lot.',
        formula: "SUM(quoted_price)\n  WHERE status = 'Open'\n    AND quote_date is within selected year",
      },
    },
    {
      // Open bids within the selected year (not all-time). projectsThisYear
      // already applies the year filter from AppContext.
      key: 'open',
      label: 'Open Opportunities',
      value: openBids.length,
      sub: `${wonProjects.length} won ${yearSubLabel} \u00B7 ${yearWinRate}% win rate`,
      icon: 'briefcase',
      accent: '#1565C0',
      iconBg: 'rgba(21,101,192,0.08)',
      yearLabel,
      explain: {
        title: 'Open Opportunities',
        body: 'Count of bids still in play - Open bids with a quote date in the selected year. The same rows as the open bids on Total Pipeline and the sidebar Opportunities badge.',
        formula: "COUNT(*)\n  WHERE status = 'Open'\n    AND quote_date is within selected year",
      },
    },
    // The "Engineers Active" card lived here. Removed \u2014 headcount belongs on
    // the Engineers tab, which owns the roster and the calendar behind it; a
    // second copy on the dashboard only ever drifted from it.
  ];
  // the week boxes the person may see (week-boxes / week-collected areas)
  const weekKpi = Object.fromEntries(lastWeek.kpis.map((k) => [k.key, k]));

  /* ── Recently Viewed ── */
  const recentItems = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('globalops_recent') || '[]');
    } catch {
      return [];
    }
  }, []);

  /* The Needs Attention pair (Remaining PO Balance + Quotes going quiet) and
     the /api/finance/po-remaining fetch behind it lived here. Removed - that
     money now reads on the Active Projects table, in the row it belongs to. */

  /* Initial-load skeleton: shimmer placeholders instead of a misleading
     "$0 / 0 bids" flash while the first fetch is in flight. */
  if (loading && (!allProjects || allProjects.length === 0)) {
    return (
      <div aria-busy="true" aria-label="Loading dashboard">
        <div className="kpi-grid-6" style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 16, marginBottom: 20 }}>
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="go-skel" style={{ height: 118 }} />
          ))}
        </div>
        <div className="dashboard-grid-main" style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr', gap: 16, marginBottom: 16 }}>
          <div className="go-skel" style={{ height: 280 }} />
          <div className="go-skel" style={{ height: 280 }} />
        </div>
        <div className="dashboard-grid-bottom" style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr 1fr', gap: 16 }}>
          {[0, 1, 2].map((i) => (
            <div key={i} className="go-skel" style={{ height: 220 }} />
          ))}
        </div>
      </div>
    );
  }

  /* ── COMPARE MODE: side-by-side columns, one per selected year. ── */
  if (compareYears && compareYears.length >= 2) {
    const cols = Math.min(compareYears.length, 3);
    return (
      <div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 14,
            background: '#fff',
            border: '1px solid #ECECEC',
            borderRadius: 10,
            padding: '12px 18px',
          }}
        >
          <div>
            <div style={{ fontSize: 10, fontWeight: 800, color: '#1B5E20', letterSpacing: 1.5, textTransform: 'uppercase' }}>
              Compare Mode
            </div>
            <div style={{ fontSize: 14, fontWeight: 800, color: '#0D0D0D', marginTop: 3 }}>Side-by-side · {compareYears.join(' vs ')}</div>
          </div>
          <button
            onClick={() => setCompareYears(null)}
            style={{
              background: '#fff',
              color: '#B71C1C',
              border: '1px solid #FFCDD2',
              padding: '7px 14px',
              borderRadius: 6,
              fontSize: 11,
              fontWeight: 700,
              cursor: 'pointer',
              fontFamily: 'Montserrat',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <Icon name="x" size={11} color="#B71C1C" /> Exit Compare
          </button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: 14 }}>
          {compareYears.map((y) => (
            <CompareColumn key={y} year={y} allProjects={allProjects} allEngineers={allEngineers} onNavigate={onNavigate} />
          ))}
        </div>
      </div>
    );
  }

  /* Every block of the Dashboard as a widget (components/dashboard/layout).
     allowed: false = the person may not see it (never drawn, never listed);
     empty: nothing to show now (a quiet line in its place, so the page never
     jumps when edit mode starts or ends).
     The KPI boxes are one widget each, so they can be moved apart. */
  const kpiWidget = (kpi) => ({ allowed: !!kpi, kind: 'kpi', render: () => <KpiCard kpi={kpi} /> });
  const widgets = {
    recent: {
      allowed: true,
      empty: recentItems.length === 0,
      emptyText: 'Recently viewed: the records you open show here',
      render: () => <RecentlyViewed items={recentItems} onNavigate={onNavigate} />,
    },
    'kpi-pipeline': kpiWidget(kpis[0]),
    'kpi-open': kpiWidget(kpis[1]),
    'week-quoted': kpiWidget(weekKpi.quoted),
    'week-pos': kpiWidget(weekKpi.pos),
    'week-collected': kpiWidget(weekKpi.collected),
    'week-invoices': kpiWidget(weekKpi.invoices),
    // My tasks: the signed-in user's open tasks on leads and opportunities (T-01)
    'my-tasks': { allowed: true, render: () => <MyActionsCard fill /> },
    // leads and open opportunities with no open task at all (T-05)
    'no-next-step': { allowed: true, render: () => <NoNextStepCard fill /> },
    // tasks on WON projects - PM work, kept as its own list (Paul Q10)
    'project-tasks': {
      allowed: true,
      render: () => (
        <TasksCard
          fill
          onOpen={(t) => {
            window.location.hash = `#/projects?project=${encodeURIComponent(t.entity_id)}`;
          }}
        />
      ),
    },
    'active-projects': {
      allowed: true,
      render: () => (
        <ActiveProjectsCard
          fill
          projects={activeProjects}
          onOpen={(p) => onNavigate('projects', { project: p.id })}
          onContext={(p, x, y) => setQuickTarget({ row: p, x, y })}
          onViewAll={() => onNavigate('projects')}
        />
      ),
    },
    utilization: {
      allowed: true,
      render: () => (
        <div style={{ ...CARD, height: '100%', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div style={{ padding: '14px 20px', borderBottom: '1px solid #F0F0F0', flexShrink: 0 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#0D0D0D' }}>Utilization</span>
            <div style={{ fontSize: 10, color: '#5A5A5A', marginTop: 2 }}>
              From the Manpower calendar · 2 months back to 2 forward · internal engineers in the tables that count
            </div>
          </div>
          {/* the chart takes the widget's size; an unreadable calendar would otherwise draw as 0% booked */}
          <div style={{ padding: '10px 16px 12px', flex: 1, minHeight: 0 }}>
            {calendarFailed ? (
              <div style={{ fontSize: 11.5, color: '#9AA29B', padding: 20 }}>{CALENDAR_FAILED}</div>
            ) : (
              <UtilizationChart data={utilizationData} />
            )}
          </div>
        </div>
      ),
    },
  };

  return (
    <div>
      {/* The widgets, where this person put them. The default is the old
          fixed Dashboard: the six KPI boxes three to a row, then My tasks /
          No next step / Project tasks, then Active Projects and Utilization -
          My tasks right under the KPIs on every screen (Paul: "if the next
          step isn't visible when I log in, it doesn't happen"). */}
      <DashboardGrid layout={layout} widgets={widgets} onSave={saveLayout} />
      {lastWeek.window}

      {/* right-click a project row: task / comment / flag, same as Projects */}
      <QuickAddMenu
        target={quickTarget}
        onClose={() => setQuickTarget(null)}
        onAdded={(m) => {
          setToast(m);
          setTimeout(() => setToast(null), 2200);
        }}
        onSetHealth={(row, key) => updateProject(row.id, { health: key })}
      />
      {toast && (
        <div
          style={{
            position: 'fixed',
            bottom: 22,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 120,
            background: '#0D0D0D',
            color: '#fff',
            padding: '9px 18px',
            borderRadius: 8,
            fontSize: 11.5,
            fontWeight: 700,
            fontFamily: 'Montserrat',
            boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
          }}
        >
          {toast}
        </div>
      )}
    </div>
  );
}

const typeColorMap = { project: '#1B5E20', opportunity: '#1565C0', account: '#6A1B9A' };
const typeViewMap = { project: 'projects', opportunity: 'opportunities', account: 'crm' };

/** The Recently Viewed chips (a widget of their own). */
function RecentlyViewed({ items, onNavigate }) {
  return (
    // the widget's height is fixed: more chips than fit scroll inside it (a little padding keeps focus rings whole)
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        flexWrap: 'wrap',
        height: '100%',
        boxSizing: 'border-box',
        alignContent: 'center',
        overflowY: 'auto',
        padding: '3px 4px',
      }}
    >
      <span
        style={{
          fontSize: 9,
          fontWeight: 700,
          color: '#5A5A5A',
          letterSpacing: 1.2,
          textTransform: 'uppercase',
          marginRight: 4,
          fontFamily: "'Montserrat', sans-serif",
        }}
      >
        Recently Viewed
      </span>
      {items.map((item, i) => (
        <div
          key={`${item.type}-${item.id}-${i}`}
          role="button"
          tabIndex={0}
          className="go-focus"
          onClick={() => onNavigate(typeViewMap[item.type] || 'dashboard')}
          onKeyDown={(e) => e.key === 'Enter' && onNavigate(typeViewMap[item.type] || 'dashboard')}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            background: '#fff',
            border: '1px solid #E8E8E8',
            borderRadius: 20,
            padding: '4px 12px',
            cursor: 'pointer',
            transition: 'all 0.15s',
            fontFamily: "'Montserrat', sans-serif",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = '#1B5E20';
            e.currentTarget.style.background = '#F0F2F0';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = '#E8E8E8';
            e.currentTarget.style.background = '#fff';
          }}
        >
          <div style={{ width: 6, height: 6, borderRadius: '50%', background: typeColorMap[item.type] || '#999', flexShrink: 0 }} />
          <span style={{ fontSize: 10, fontWeight: 600, color: '#0D0D0D' }}>{item.label}</span>
        </div>
      ))}
    </div>
  );
}
